import JsonLd from 'components/common/JsonLd';
import { usdToNumber } from 'helpers/NumberFormats';

export interface ProductCardJsonLdProps {
  productName: string;
  brandName?: string;
  colorName?: string;
  /** Display price string (e.g. "$49.50") or number; coerced to a plain numeric string for the Offer. */
  price?: string | number;
  /** Product/offer URL — the same link target the card renders. */
  url?: string;
  image?: string;
  /** Maps to schema.org sku. */
  productId: string | number;
  /** Units in stock. Availability mirrors PDP's signal (see Price.tsx): > 0 → InStock, else OutOfStock. */
  onHand?: number;
  /** Overall product rating; emitted as aggregateRating together with reviewCount when both are positive. */
  productRating?: string | number;
  reviewCount?: string | number;
}

/**
 * Emits a JSON-LD `Product` for a product card, replacing the schema.org microdata the brand-page
 * cards (SmallProductCard, MelodyCardProduct) previously rendered inline. This aligns brand pages with
 * the search/PLP cards, which already emit JSON-LD via `withJsonLdProduct` (JsonLDProductWrapper).
 *
 * Google validates any Product it extracts and requires one of offers/review/aggregateRating, so the
 * Product is only emitted when a price is present (yielding an Offer with `availability`). This avoids
 * reintroducing the "Either offers, review, or aggregateRating should be specified" error that a bare
 * Product — the exact thing the leftover microdata produced — triggers in Search Console.
 */
export default function ProductCardJsonLd({
  productName,
  brandName,
  colorName,
  price,
  url,
  image,
  productId,
  onHand,
  productRating,
  reviewCount
}: ProductCardJsonLdProps) {
  // Without a name + price there is no valid Offer-bearing Product to emit.
  if (!productName || price === undefined || price === null || price === '') {
    return null;
  }

  const isAvailable = !!onHand && Number(onHand) > 0;
  const hasAggregateRating =
    productRating !== undefined && parseFloat(String(productRating)) > 0 && reviewCount !== undefined && parseInt(String(reviewCount), 10) > 0;

  const data = {
    '@type': 'Product',
    'name': productName,
    ...(brandName && { brand: { '@type': 'Brand', 'name': brandName } }),
    ...(colorName && { color: colorName }),
    ...(image && { image }),
    ...(productId !== undefined && productId !== null && { sku: String(productId) }),
    'offers': {
      '@type': 'Offer',
      'price': usdToNumber(price),
      'priceCurrency': 'USD',
      'availability': `https://schema.org/${isAvailable ? 'InStock' : 'OutOfStock'}`,
      ...(url && { url })
    },
    ...(hasAggregateRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        'ratingValue': String(productRating),
        'reviewCount': String(reviewCount),
        'ratingCount': String(reviewCount)
      }
    }),
    ...(url && { url })
  };

  return <JsonLd data={data} />;
}
